<template>
  <!-- cas-149c: in-app browser escape banner. Only rendered on client after mount. -->
  <ClientOnly>
    <Transition name="iab-slide">
      <div v-if="visible" class="iab-banner" role="alert" aria-live="polite">
        <svg
          v-if="isIOS && !copied"
          class="iab-banner__arrow"
          viewBox="0 0 46 44"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M4 38 C14 20 28 14 39 6"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
          />
          <path
            d="M31 5 L40 5 L40 14"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <div class="iab-banner__content">
          <div class="iab-banner__main-row">
            <q-icon name="open_in_browser" size="20px" class="iab-banner__icon" />

            <div class="iab-banner__text">
              <span class="iab-banner__message">{{ $t('inAppBrowserBanner.message') }}</span>
            </div>

            <div class="iab-banner__actions">
              <!--
                Android: navigate to intent:// URL — asks the OS to open in the
                default browser. Regular <a> is required here; q-btn would prevent
                default on the anchor's click handler.
              -->
              <a
                v-if="isAndroid"
                :href="escapeUrl"
                class="iab-banner__open-btn q-btn q-btn--flat q-btn--rectangle q-btn--actionable text-weight-bold"
                @click.stop
              >
                {{ $t('inAppBrowserBanner.openAndroid') }}
              </a>

              <!--
                iOS: can't force Safari programmatically. Copying the URL is the
                reliable path; the instructions also point users to Instagram's
                browser menu when it offers "Open in external browser".
              -->
              <q-btn
                v-else-if="isIOS && !copied"
                flat
                dense
                no-caps
                :label="$t('inAppBrowserBanner.clickHere')"
                class="iab-banner__open-btn text-weight-bold"
                @click="copyUrl"
              />

              <!--
                Generic in-app browser (not iOS or Android — rare but exists on
                desktops, e.g. Electron shells, Slack desktop webview).
                Provide the raw URL in an anchor so the user can open it manually.
              -->
              <a
                v-else-if="!isIOS"
                :href="escapeUrl"
                target="_blank"
                rel="noopener"
                class="iab-banner__open-btn q-btn q-btn--flat q-btn--rectangle q-btn--actionable text-weight-bold"
              >
                {{ $t('inAppBrowserBanner.openGeneric') }}
              </a>
            </div>

            <q-btn
              flat
              dense
              round
              icon="close"
              size="sm"
              class="iab-banner__dismiss"
              :aria-label="$t('inAppBrowserBanner.dismiss')"
              @click="dismiss"
            />
          </div>

          <div v-if="isIOS && !copied" class="iab-banner__instructions">
            <q-icon name="more_horiz" size="18px" class="iab-banner__menu-icon" />
            <span>{{ $t('inAppBrowserBanner.iosInstructions') }}</span>
          </div>

          <div v-if="isIOS && copied" class="iab-banner__success">
            <q-icon name="check_circle" size="20px" class="iab-banner__success-icon" />
            <span>{{ $t('inAppBrowserBanner.linkCopiedSuccess') }}</span>
          </div>
        </div>
      </div>
    </Transition>
  </ClientOnly>
</template>

<script setup lang="ts">
import { useInAppBrowserGuard } from '~/composables/useInAppBrowserGuard';

const SESSION_KEY = 'iab_banner_dismissed';

const { isInAppBrowser, isIOS, isAndroid, escapeUrl } = useInAppBrowserGuard();

const dismissed = ref(false);
const copied = ref(false);

// In-browser (IG/TikTok WebView) experience is intentional now — keep the
// escape banner off so it does not distract from the optimized flow.
const BANNER_ENABLED = false;
const visible = computed(() => BANNER_ENABLED && isInAppBrowser.value && !dismissed.value);

onMounted(() => {
  // Respect session-level dismissal so the banner doesn't re-appear on every
  // page navigation (SPA navigation re-runs onMounted on the layout).
  if (sessionStorage.getItem(SESSION_KEY) === '1') {
    dismissed.value = true;
  }
});

function dismiss() {
  dismissed.value = true;
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // sessionStorage may be blocked in some WebView environments — ignore.
  }
}

async function copyUrl() {
  try {
    await navigator.clipboard.writeText(escapeUrl.value || window.location.href);
    copied.value = true;
  } catch {
    // Clipboard API not available — fall back to no-op; the user can manually
    // copy from the address bar.
  }
}
</script>

<style scoped>
.iab-banner {
  position: sticky;
  top: 0;
  z-index: 9000;
  overflow: visible;
  color: #1a1a1a;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
  padding: 10px 12px;
  /* Ensure it sits above page content without interfering with q-header */
}

.iab-banner__arrow {
  position: absolute;
  top: -28px;
  right: 22px;
  width: 46px;
  height: 44px;
  color: #1a1a1a;
  pointer-events: none;
}

.iab-banner__content {
  max-width: 800px;
  margin: 0 auto;
}

.iab-banner__main-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.iab-banner__icon {
  color: #7c6af7;
  flex-shrink: 0;
}

.iab-banner__text {
  flex: 1;
  min-width: 0;
}

.iab-banner__message {
  font-size: 0.875rem;
  line-height: 1.3;
  color: #1a1a1a;
  font-weight: 600;
  display: block;
}

.iab-banner__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.iab-banner__open-btn {
  font-size: 0.8125rem;
  /* cas-d52c: the lilac link ink, readable on the banner's solid lilac fill in both themes. */
  color: var(--g-accent-purple-light);
  /* Override default q-btn anchor decoration */
  text-decoration: none;
  padding: 5px 10px;
  border: 1px solid var(--g-accent-purple-stroke);
  border-radius: 8px;
  background: var(--g-accent-purple-soft);
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.iab-banner__open-btn:hover {
  background: var(--g-accent-purple-soft);
}

.iab-banner__dismiss {
  color: #4b5563;
  flex-shrink: 0;
}

.iab-banner__instructions {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 6px;
  padding-left: 28px;
  color: #333;
  font-size: 0.8125rem;
  line-height: 1.35;
}

.iab-banner__menu-icon {
  color: #5f4be8;
  flex-shrink: 0;
  margin-top: 1px;
}

.iab-banner__success {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding: 10px 12px;
  border-left: 4px solid #20b486;
  border-radius: 8px;
  background: #eefbf6;
  color: #12352b;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.35;
}

.iab-banner__success-icon {
  color: #20b486;
  flex-shrink: 0;
  margin-top: 1px;
}

/* Slide-in transition */
.iab-slide-enter-active,
.iab-slide-leave-active {
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.iab-slide-enter-from,
.iab-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
